import {useMutation, useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {
  AssigneeSchema,
  AssigneesResponseSchema,
  type AssigneesResponse,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/assignees'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'

import {pullRequestsOverviewRoute} from '../routes/overview-route'

export type MutationData = {
  /** Global relay node ids of assignees to add (the format the shared assignee picker produces). */
  addAssignees: string[]
  /** Global relay node ids of assignees to remove. */
  removeAssignees: string[]
  /** Next selection, used to paint the sidebar optimistically before the server responds. */
  nextAssignees: AssigneePickerAssignee[]
}

type Callbacks = {
  onError?: (error: Error) => void
}

export function useModifyAssignees({onError}: Callbacks = {}) {
  const assigneesQueryKey = useRouteQueryKey(pullRequestsOverviewRoute, 'assignees')
  const apiURL = usePageDataUrl(PageData.modifyAssignees)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const response = await fetchWithErrorHandling(apiURL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: {addAssignees: data.addAssignees, removeAssignees: data.removeAssignees},
      })
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json
    },
    onMutate: async ({nextAssignees}) => {
      if (!assigneesQueryKey) return

      await queryClient.cancelQueries({queryKey: assigneesQueryKey})
      const previous = queryClient.getQueryData<AssigneesResponse>(assigneesQueryKey)

      const optimistic = nextAssignees.map((assignee, index) =>
        create(AssigneeSchema, {
          // Real database id when the picker has it; a negative placeholder keeps React
          // keys unique for freshly added assignees until the refetch lands.
          id: assignee.databaseId ? BigInt(assignee.databaseId) : BigInt(-(index + 1)),
          displayLogin: assignee.login,
          avatarUrl: assignee.avatarUrl,
          nodeId: assignee.id,
          // Non-empty `slug` marks a bot/agent for read-only list to paint it as bot.
          slug: assignee.__typename === 'Bot' || assignee.isAgent || assignee.isCopilot ? assignee.login : '',
        }),
      )

      queryClient.setQueryData<AssigneesResponse>(assigneesQueryKey, () =>
        create(AssigneesResponseSchema, {
          assignees: optimistic,
        }),
      )

      return {previous}
    },
    onError: (e: Error, _vars, context) => {
      if (assigneesQueryKey && context?.previous) {
        queryClient.setQueryData(assigneesQueryKey, context.previous)
      }
      onError?.(e)
    },
    onSettled: () => {
      if (!assigneesQueryKey) return
      return queryClient.invalidateQueries({queryKey: assigneesQueryKey})
    },
  })
}
